import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { inventoryAPI } from '../../services/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const PickPackForm = ({ formik }) => {
  const [showAddItem, setShowAddItem] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Initial item state
  const initialItemState = {
    productSku: '',
    productName: '',
    quantity: 1,
    specificSerials: [],
    specificMacs: [],
    packagingPreference: 'standard',
    notes: ''
  };
  
  const [currentItem, setCurrentItem] = useState({ ...initialItemState });
  const [specificIdentifiers, setSpecificIdentifiers] = useState('');
  const [identifierType, setIdentifierType] = useState('serial');

  // Fetch product suggestions when user types in search
  const { data: productSuggestions, isLoading } = useQuery(
    ['productSuggestions', searchTerm],
    () => inventoryAPI.getInventory({ search: searchTerm, limit: 5, customerId: formik.values.customerId }),
    {
      enabled: searchTerm.length > 2,
      keepPreviousData: true
    }
  );
  
  // Handle date change
  const handleDateChange = (date) => {
    formik.setFieldValue('scheduledDate', date);
  };
  
  // Handle product selection
  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setCurrentItem({
      ...currentItem,
      productSku: product.productSku,
      productName: product.productName
    });
    setSearchTerm('');
  };
  
  // Add specific identifiers (serial numbers or MAC addresses)
  const handleAddIdentifiers = () => {
    if (!specificIdentifiers.trim()) return;
    
    const identifiers = specificIdentifiers.split(',').map(id => id.trim()).filter(id => id);
    
    if (identifierType === 'serial') {
      setCurrentItem({
        ...currentItem,
        specificSerials: [...currentItem.specificSerials, ...identifiers]
      });
    } else {
      setCurrentItem({
        ...currentItem,
        specificMacs: [...currentItem.specificMacs, ...identifiers]
      });
    }
    
    setSpecificIdentifiers('');
  };
  
  // Remove a specific identifier
  const handleRemoveIdentifier = (index, type) => {
    if (type === 'serial') {
      const updatedSerials = [...currentItem.specificSerials];
      updatedSerials.splice(index, 1);
      setCurrentItem({
        ...currentItem,
        specificSerials: updatedSerials
      });
    } else {
      const updatedMacs = [...currentItem.specificMacs];
      updatedMacs.splice(index, 1);
      setCurrentItem({
        ...currentItem,
        specificMacs: updatedMacs
      });
    }
  };
  
  // Handle adding item to the request
  const handleAddItem = () => {
    if (!currentItem.productSku || !currentItem.productName || currentItem.quantity <= 0) {
      return;
    }
    
    // Add the current item to the line items array
    formik.setFieldValue('lineItems', [
      ...formik.values.lineItems,
      { ...currentItem }
    ]);
    
    // Reset current item
    setCurrentItem({ ...initialItemState });
    setSelectedProduct(null);
    setShowAddItem(false);
  };
  
  // Handle removing item from the request
  const handleRemoveItem = (index) => {
    const updatedItems = [...formik.values.lineItems];
    updatedItems.splice(index, 1);
    formik.setFieldValue('lineItems', updatedItems);
  };
  
  // Handle packaging preference change
  const handlePackagingPreferenceChange = (preference) => {
    setCurrentItem({
      ...currentItem,
      packagingPreference: preference
    });
  };
  
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Pick & Pack Request Details</h3>
      
      {/* Scheduled Date */}
      <div>
        <label htmlFor="scheduledDate" className="block text-sm font-medium text-gray-700">
          Requested Completion Date
        </label>
        <div className="mt-1">
          <DatePicker
            selected={formik.values.scheduledDate ? new Date(formik.values.scheduledDate) : null}
            onChange={handleDateChange}
            minDate={new Date()}
            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
            placeholderText="Select date"
          />
        </div>
        {formik.touched.scheduledDate && formik.errors.scheduledDate && (
          <p className="mt-2 text-sm text-red-600">{formik.errors.scheduledDate}</p>
        )}
      </div>
      
      {/* Line Items */}
      <div>
        <div className="flex justify-between items-center">
          <h4 className="text-base font-medium text-gray-900">Items to Pick & Pack</h4>
          <button
            type="button"
            onClick={() => setShowAddItem(true)}
            className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Add Item
          </button>
        </div>
        
        {formik.values.lineItems.length === 0 ? (
          <div className="mt-2 text-sm text-gray-500 bg-gray-50 p-4 rounded-md">
            No items added yet. Click "Add Item" to start adding products.
          </div>
        ) : (
          <div className="mt-2 border border-gray-200 rounded-md divide-y divide-gray-200">
            {formik.values.lineItems.map((item, index) => (
              <div key={index} className="p-4 flex justify-between items-start">
                <div>
                  <p className="font-medium">{item.productName}</p>
                  <p className="text-sm text-gray-500">SKU: {item.productSku}</p>
                  <p className="text-sm text-gray-500">
                    Quantity: {item.quantity} | Packaging: {item.packagingPreference.replace('-', ' ')}
                  </p>
                  
                  {item.specificSerials.length > 0 && (
                    <div className="mt-1">
                      <p className="text-sm text-gray-700">Serial Numbers:</p>
                      <p className="text-xs text-gray-500">{item.specificSerials.join(', ')}</p>
                    </div>
                  )}
                  
                  {item.specificMacs.length > 0 && (
                    <div className="mt-1">
                      <p className="text-sm text-gray-700">MAC Addresses:</p>
                      <p className="text-xs text-gray-500">{item.specificMacs.join(', ')}</p>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveItem(index)}
                  className="text-red-600 hover:text-red-900"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
        
        {formik.touched.lineItems && formik.errors.lineItems && (
          <p className="mt-2 text-sm text-red-600">{formik.errors.lineItems}</p>
        )}
      </div>
      
      {/* Add Item Form */}
      {showAddItem && (
        <div className="mt-4 bg-gray-50 p-4 rounded-md space-y-4">
          <h4 className="text-base font-medium text-gray-900">Add Item to Pick & Pack</h4>
          
          {/* Product Search */}
          <div>
            <label htmlFor="productSearch" className="block text-sm font-medium text-gray-700">
              Search Your Inventory
            </label>
            <div className="mt-1 relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Type to search products..."
              />
              {searchTerm.length > 2 && productSuggestions && (
                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-y-auto">
                  {isLoading ? (
                    <div className="p-2 text-center text-gray-500">Loading...</div>
                  ) : productSuggestions.data.length > 0 ? (
                    productSuggestions.data.map((product) => (
                      <div
                        key={product.id}
                        className="p-2 hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleProductSelect(product)}
                      >
                        <p className="font-medium">{product.productName}</p>
                        <p className="text-sm text-gray-500">SKU: {product.productSku}</p>
                        <p className="text-xs text-gray-500">Available: {product.quantity}</p>
                      </div>
                    ))
                  ) : (
                    <div className="p-2 text-center text-gray-500">
                      No products found. You can manually enter details below.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Product Details */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="productSku" className="block text-sm font-medium text-gray-700">
                Product SKU
              </label>
              <input
                type="text"
                value={currentItem.productSku}
                onChange={(e) => setCurrentItem({...currentItem, productSku: e.target.value})}
                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            
            <div>
              <label htmlFor="productName" className="block text-sm font-medium text-gray-700">
                Product Name
              </label>
              <input
                type="text"
                value={currentItem.productName}
                onChange={(e) => setCurrentItem({...currentItem, productName: e.target.value})}
                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            
            <div>
              <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
                Quantity
              </label>
              <input
                type="number"
                min="1"
                value={currentItem.quantity}
                onChange={(e) => setCurrentItem({...currentItem, quantity: parseInt(e.target.value) || 0})}
                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>
          </div>
          
          {/* Packaging Preferences */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Packaging Preference
            </label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <div 
                className={`p-3 border rounded-md cursor-pointer hover:bg-gray-50 ${currentItem.packagingPreference === 'standard' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'}`}
                onClick={() => handlePackagingPreferenceChange('standard')}
              >
                <p className="font-medium">Standard</p>
                <p className="text-xs text-gray-500">Default packaging</p>
              </div>
              <div 
                className={`p-3 border rounded-md cursor-pointer hover:bg-gray-50 ${currentItem.packagingPreference === 'minimal' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'}`}
                onClick={() => handlePackagingPreferenceChange('minimal')}
              >
                <p className="font-medium">Minimal</p>
                <p className="text-xs text-gray-500">Eco-friendly option</p>
              </div>
              <div 
                className={`p-3 border rounded-md cursor-pointer hover:bg-gray-50 ${currentItem.packagingPreference === 'premium' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'}`}
                onClick={() => handlePackagingPreferenceChange('premium')}
              >
                <p className="font-medium">Premium</p>
                <p className="text-xs text-gray-500">Enhanced protection</p>
              </div>
            </div>
          </div>
          
          {/* Specific Serial/MAC Numbers */}
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-2">
              Specific Identifiers (Optional)
            </h5>
            
            <div className="flex space-x-2 mb-2">
              <select
                value={identifierType}
                onChange={(e) => setIdentifierType(e.target.value)}
                className="block w-40 py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="serial">Serial Numbers</option>
                <option value="mac">MAC Addresses</option>
              </select>
              
              <input
                type="text"
                value={specificIdentifiers}
                onChange={(e) => setSpecificIdentifiers(e.target.value)}
                placeholder="Enter comma-separated values"
                className="flex-1 focus:ring-indigo-500 focus:border-indigo-500 block shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
              
              <button
                type="button"
                onClick={handleAddIdentifiers}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Add
              </button>
            </div>
            
            {/* Display added serial numbers */}
            {currentItem.specificSerials.length > 0 && (
              <div className="mb-2">
                <p className="text-sm font-medium text-gray-700">Serial Numbers:</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {currentItem.specificSerials.map((serial, index) => (
                    <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      {serial}
                      <button
                        type="button"
                        className="ml-1.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-gray-400 hover:text-gray-500"
                        onClick={() => handleRemoveIdentifier(index, 'serial')}
                      >
                        <span className="sr-only">Remove</span>
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Display added MAC addresses */}
            {currentItem.specificMacs.length > 0 && (
              <div className="mb-2">
                <p className="text-sm font-medium text-gray-700">MAC Addresses:</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {currentItem.specificMacs.map((mac, index) => (
                    <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      {mac}
                      <button
                        type="button"
                        className="ml-1.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-gray-400 hover:text-gray-500"
                        onClick={() => handleRemoveIdentifier(index, 'mac')}
                      >
                        <span className="sr-only">Remove</span>
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Notes */}
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
              Notes (Optional)
            </label>
            <textarea
              rows="2"
              value={currentItem.notes}
              onChange={(e) => setCurrentItem({...currentItem, notes: e.target.value})}
              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            ></textarea>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                setShowAddItem(false);
                setCurrentItem({...initialItemState});
              }}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddItem}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
              disabled={!currentItem.productSku || !currentItem.productName || currentItem.quantity <= 0}
            >
              Add to Request
            </button>
          </div>
        </div>
      )}
      
      {/* Special Instructions */}
      <div>
        <label htmlFor="specialInstructions" className="block text-sm font-medium text-gray-700">
          Special Instructions (Optional)
        </label>
        <textarea
          rows="3"
          value={formik.values.specialInstructions || ''}
          onChange={(e) => formik.setFieldValue('specialInstructions', e.target.value)}
          className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
        ></textarea>
      </div>
    </div>
  );
};

export default PickPackForm; 